import {
  getPortfolioAccountStatus,
  isPortfolioAccountAvailableForDate,
  PORTFOLIO_ACCOUNT_STATUS_LABELS,
  PORTFOLIO_ACCOUNT_STATUS_OBJ,
} from "@signals/shared";
import type { Account } from "./portfolio.types";

const RU = "ru-RU";

/** Name with an explicit closed mark; `cutoff` limits the mark to accounts closed by that date. */
export function accountLabel(account: Pick<Account, "name" | "closedOn">, cutoff?: string): string {
  const closed = account.closedOn != null &&
    (cutoff === undefined || getPortfolioAccountStatus(account.closedOn, cutoff) === PORTFOLIO_ACCOUNT_STATUS_OBJ.CLOSED);
  return closed ? `${account.name} (${PORTFOLIO_ACCOUNT_STATUS_LABELS.closed[RU]!.toLowerCase()} ${account.closedOn})` : account.name;
}

/** An operation is accepted when every operation date is not later than the closure date. */
export function isAccountAvailableForDates(account: Pick<Account, "closedOn"> | undefined, dates: readonly string[]): boolean {
  return dates.every((date) => !date || isPortfolioAccountAvailableForDate(account?.closedOn, date));
}

/**
 * Account choices for creating or editing an operation. Availability follows the operation dates,
 * not the catalog status: a closed account stays selectable for dates up to its closure date.
 * The current choice and the account of an edited record stay listed (marked) so they are never
 * shown as unknown and never replaced silently.
 */
export function operationAccountOptions(
  accounts: readonly Account[],
  dates: readonly string[],
  keepAccountIds: ReadonlyArray<string | undefined> = [],
): { value: string; label: string }[] {
  const keep = new Set(keepAccountIds.filter((id): id is string => Boolean(id)));
  return accounts
    .filter((account) => keep.has(account.id) || isAccountAvailableForDates(account, dates))
    .map((account) => ({ value: account.id, label: accountLabel(account) }));
}

/** Validation text for a chosen account that cannot take an operation on one of the dates. */
export function accountUnavailableMessage(account: Account | undefined, dates: readonly string[]): string | null {
  if (!account || isAccountAvailableForDates(account, dates)) return null;
  return `Счёт «${account.name}» закрыт ${account.closedOn}: операции позднее этой даты недоступны`;
}
